// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaButtonClickEvent } from "@stoneage/App/Utilities/DataLayer";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import xBackgroundImg from "@stoneage/XDL/Utilities/xBackgroundImg";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    largeImageSrc = "largeImageSrc",
    mediumImageSrc = "mediumImageSrc",
    smallImageSrc = "smallImageSrc",
    title = "title",
    titleStyle = "titleStyle",
    copy = "copy",
    colorTheme = "colorTheme",
    links = "links",
}

const enum linkFields {
    url = "url",
    linkText = "linkText",
    linkId = "linkId",
    openInNewTab = "openInNewTab",
    gaTrackClickEvent = "gaTrackClickEvent",
    gaClickEvent = "gaClickEvent",
}

type LinkModel = {
    fields: {
        url: LinkFieldValue;
        linkText: string;
        linkId: string;
        openInNewTab: boolean;
        gaTrackClickEvent: boolean;
        gaClickEvent: string;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.largeImageSrc]: string;
        [fields.mediumImageSrc]: string;
        [fields.smallImageSrc]: string;
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.copy]: string;
        [fields.colorTheme]: "light" | "dark";
        [fields.links]: LinkModel[];
    };
}

type Props = OwnProps;
export interface InfoLinkStyles {
    container?: InjectableCss;
    contentContainer?: InjectableCss;
    title?: TypographyProps;
    copy?: TypographyProps;
    linksContainer?: InjectableCss;
    linksSeparator?: InjectableCss;
    button?: ButtonPresentationProps;
    link?: LinkProps;
}

export const infoLinkStyles: InfoLinkStyles = {
    container: {
        css: css`
            display: flex;
            box-shadow: 0 -8px 0 0 ${color("common.ui8")};
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    contentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 20px;
            padding-bottom: 24px;
        `,
    },
    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    copy: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    linksContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
        `,
    },
    linksSeparator: {
        css: css`
            height: 16px;
            width: 1px;
            background-color: ${color("common.ui5")};
            align-self: center;
        `,
    },
    button: {
        mergeCss: true,
        css: css`
            width: fit-content;
        `,
    },
};

const styles = infoLinkStyles;

const InfoLink = ({
    id,
    fields: { largeImageSrc, mediumImageSrc, smallImageSrc, title, titleStyle, copy, links, colorTheme },
}: Props) => {
    const getThemeTextColor = () => (colorTheme === "light" ? "common.black" : "common.white");
    const getThemeCopyColor = () => (colorTheme === "light" ? "text.main" : "common.ui5");
    const { customerNumber, impersonation } = useAppSelector(state => ({
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    }));

    /**
     * Shows first link as button, and rest as regular links
     * @returns JSX.Element
     */
    const linksSection = () => {
        if (!links?.length) {
            return null;
        }

        const buttonLink = links[0];
        const {
            fields: {
                url,
                linkText: buttonText,
                linkId: buttonLinkId,
                openInNewTab: buttonOpenInNewTab,
                gaClickEvent: buttonGaClickEvent,
                gaTrackClickEvent: buttonGaTrackClickEvent,
            },
        } = buttonLink;
        const buttonLinkUrl = useGetLink(url);

        const bottomLinks = links.slice(1);

        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                `}
            >
                <Button
                    {...({ href: buttonLinkUrl.url, target: buttonOpenInNewTab ? "_blank" : "_self" } as any)}
                    {...styles.button}
                    {...(buttonLinkId && { id: `${buttonLinkId}_${id}` })}
                    {...(buttonGaTrackClickEvent &&
                        buttonGaClickEvent && {
                            onClick: () => {
                                trackGaButtonClickEvent(
                                    buttonGaClickEvent,
                                    buttonText || buttonLinkUrl.title || "",
                                    impersonation,
                                    customerNumber,
                                );
                            },
                        })}
                >
                    {buttonText}
                </Button>
                <StyledWrapper {...styles.linksContainer}>
                    {bottomLinks?.map(
                        ({ fields: { url, linkText, openInNewTab, gaClickEvent, gaTrackClickEvent, linkId } }, idx) => {
                            const linkUrl = useGetLink(url);
                            return (
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        gap: 20px;
                                    `}
                                    key={idx}
                                >
                                    <Link
                                        href={linkUrl.url}
                                        target={openInNewTab ? "_blank" : "_self"}
                                        {...styles.link}
                                        color={getThemeTextColor()}
                                        {...(linkId && { id: `${linkId}${idx}_${id}` })}
                                        {...(gaTrackClickEvent &&
                                            gaClickEvent && {
                                                onClick: () => {
                                                    trackGaButtonClickEvent(
                                                        gaClickEvent,
                                                        linkText || linkUrl.title || "",
                                                        impersonation,
                                                        customerNumber,
                                                    );
                                                },
                                            })}
                                    >
                                        {linkText || linkUrl.title}
                                    </Link>
                                    {idx < bottomLinks.length - 1 && <StyledWrapper {...styles.linksSeparator} />}
                                </StyledWrapper>
                            );
                        },
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper
            className="info-link"
            {...styles.container}
            css={css`
                ${styles.container?.css};
                background-color: ${colorTheme === "light" ? "transparent" : getColor("common.ui10")};
            `}
        >
            <StyledWrapper
                css={css`
                    width: 244px;
                    min-width: 244px;
                    background-image: ${xBackgroundImg(largeImageSrc)};
                    background-repeat: no-repeat;
                    background-size: contain;
                    @media all and (max-width: 1024px) {
                        width: 100%;
                        height: 116px;
                        background-size: cover;
                        background-image: ${xBackgroundImg(mediumImageSrc)};
                    }
                    @media all and (max-width: 481px) {
                        background-image: ${xBackgroundImg(smallImageSrc)};
                    }
                `}
            ></StyledWrapper>
            <StyledWrapper {...styles.contentContainer}>
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getThemeTextColor()}>
                    {parse(title, parserOptions)}
                </Typography>
                <Typography {...styles.copy} color={getThemeCopyColor()}>
                    {parse(copy, parserOptions)}
                </Typography>
                {linksSection()}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoLink,
    definition: {
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.largeImageSrc,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.mediumImageSrc,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.smallImageSrc,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                getDisplay: item => `${item.fields?.linkText}`,
                fieldDefinitions: [
                    {
                        name: linkFields.url,
                        editorTemplate: "LinkField",
                        isRequired: true,
                        defaultValue: {
                            type: "Page",
                            value: "",
                        },
                    },
                    {
                        name: linkFields.linkText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        validate: (value, item) => (value.length > 40 ? "max allowed characters is 40" : null),
                    },
                    {
                        name: linkFields.linkId,
                        displayName: "Link Id",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: linkFields.openInNewTab,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: linkFields.gaTrackClickEvent,
                        displayName: "Track GA4 Click Event",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: linkFields.gaClickEvent,
                        displayName: "Event Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields[linkFields.gaTrackClickEvent],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
